import { ISSUED_PROPERTY_CODE_SOURCE } from "./issued-property-code.ts";

export interface PostHogCaptureSource {
  id: string;
  createdAtSeconds: number;
}

export interface PostHogCaptureInput {
  apiKey: string;
  distinctId: string;
  event: string;
  properties: Record<string, unknown>;
  source?: PostHogCaptureSource;
}

const DIRECT_CUSTOMER_IDENTIFIER_KEYS = new Set([
  "address",
  "propertyaddress",
  "situsaddress",
  "originaladdress",
  "normalizedaddress",
  "propertyid",
  "manualpropertyid",
  "accountnumber",
  "hcadaccountnumber",
  "userid",
  "guestemail",
  "homeowneremail",
  "contactemail",
  "email",
  "phone",
  "phonenumber",
  "mailcode",
  "mailcodeid",
  "propertycode",
  "propertycodeid",
  "qrcode",
  "qrcodeid",
  "tagid",
  "packetid",
  "guestsessionid",
  "sessionid",
  "stripesessionid",
  "stripepaymentintentid",
  "pdfurl",
]);
const SENSITIVE_QUERY_PARAMETER =
  /([?&](?:access[_-]?code|session[_-]?token|guest[_-]?token|resume|address|property[_-]?address|situs[_-]?address|original[_-]?address|normalized[_-]?address|property[_-]?id|manual[_-]?property[_-]?id|account[_-]?number|hcad[_-]?account[_-]?number|user[_-]?id|guest[_-]?email|homeowner[_-]?email|contact[_-]?email|email|phone[_-]?number|phone|mail[_-]?code|property[_-]?code|qr[_-]?code|code)=)[^&#\s]*/gi;
const IDENTIFIER_ROUTE_SEGMENT =
  /((?:https?:\/\/[^/?#\s]+)?\/(?:a|check|go|property|qr)\/)[^/?#\s]+/gi;
const PROPERTY_CODE_TOKEN = new RegExp(`\\b${ISSUED_PROPERTY_CODE_SOURCE}\\b`, "gi");
const PROPERTY_CODE_IDENTITY = new RegExp(`(?:^|[^A-Z0-9])${ISSUED_PROPERTY_CODE_SOURCE}(?:$|[^A-Z0-9])`, "i");

// Issued-code privacy follows the national code shape, including DC; it is
// independent of which jurisdictions have an active resolver or data source.

/** Remove direct customer, property, and county-account identifiers from analytics properties. */
export function sanitizePostHogProperties(
  value: Record<string, unknown>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(value).map(([key, propertyValue]) => [
      key,
      sanitizePostHogValue(propertyValue, key),
    ]),
  );
}

export function sanitizePostHogValue(value: unknown, key = ""): unknown {
  const normalizedKey = key.replace(/[_\-\s]/g, "").toLowerCase();
  if (DIRECT_CUSTOMER_IDENTIFIER_KEYS.has(normalizedKey)) return "[Filtered]";
  if (typeof value === "string") {
    return value
      .replace(SENSITIVE_QUERY_PARAMETER, "$1[Filtered]")
      .replace(IDENTIFIER_ROUTE_SEGMENT, "$1[Filtered]")
      .replace(PROPERTY_CODE_TOKEN, "[Filtered]");
  }
  if (Array.isArray(value)) {
    return value.map((entry) => sanitizePostHogValue(entry));
  }
  if (value && typeof value === "object") {
    return sanitizePostHogProperties(value as Record<string, unknown>);
  }
  return value;
}

export function isPropertyCodeIdentifier(value: unknown): boolean {
  if (typeof value !== "string") return false;
  const normalized = value.trim();
  // DEMO is the one reserved production seed outside the issued-code shapes.
  // Treat it as unsafe only when it is the complete identity so ordinary
  // diagnostics containing the word "demo" are not broadly redacted.
  return normalized.toUpperCase() === "DEMO" ||
    PROPERTY_CODE_IDENTITY.test(normalized);
}

/** Select the first usable server/provider identity without accepting a property code. */
export function selectServerPostHogDistinctId(
  candidates: readonly unknown[],
  fallback: string,
): string {
  for (const candidate of candidates) {
    if (typeof candidate !== "string") continue;
    const normalized = candidate.trim();
    if (normalized && !isPropertyCodeIdentifier(normalized)) return normalized;
  }

  if (!fallback.trim() || isPropertyCodeIdentifier(fallback)) {
    throw new Error("A non-property-code PostHog fallback identity is required");
  }
  return fallback;
}

/**
 * Reject a property code as a person identity without deriving a stable hash
 * from that low-entropy code. Webhook-backed events use the provider event as
 * a retry-stable, event-scoped pseudonym; unbacked calls remain deliberately
 * unlinkable.
 */
export async function privacySafePostHogDistinctId(
  input: Pick<PostHogCaptureInput, "distinctId" | "event" | "source">,
): Promise<string> {
  if (!isPropertyCodeIdentifier(input.distinctId)) return input.distinctId;

  if (input.source?.id && !isPropertyCodeIdentifier(input.source.id)) {
    return `fairpath_server_event_${await deterministicEventUuid(
      `posthog-event:${input.source.id}:${input.event}`,
    )}`;
  }

  return `fairpath_server_unlinked_${crypto.randomUUID()}`;
}

/** Build a retry-stable PostHog capture envelope for webhook-backed events. */
export async function buildPostHogCaptureEnvelope(input: PostHogCaptureInput) {
  const timestamp = input.source && Number.isFinite(input.source.createdAtSeconds)
    ? new Date(input.source.createdAtSeconds * 1000).toISOString()
    : new Date().toISOString();
  const uuid = input.source?.id && !isPropertyCodeIdentifier(input.source.id)
    ? await deterministicEventUuid(`${input.source.id}:${input.event}`)
    : undefined;

  return {
    api_key: input.apiKey,
    event: input.event,
    distinct_id: await privacySafePostHogDistinctId(input),
    properties: {
      ...sanitizePostHogProperties(input.properties),
      $lib: "edge-function",
    },
    timestamp,
    ...(uuid ? { uuid } : {}),
  };
}

/**
 * Send one privacy-sanitized event through PostHog's current single-event
 * endpoint. Rejections are observable without logging payloads or identifiers.
 */
export async function capturePostHogEvent(
  input: PostHogCaptureInput,
): Promise<boolean> {
  try {
    const response = await fetch("https://us.i.posthog.com/i/v0/e/", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(await buildPostHogCaptureEnvelope(input)),
      signal: AbortSignal.timeout(2_000),
    });
    if (!response.ok) {
      console.warn(
        `[posthog-capture] Event rejected: event=${input.event}, status=${response.status}`,
      );
      return false;
    }
    return true;
  } catch (error) {
    console.warn(
      `[posthog-capture] Event delivery failed: event=${input.event}, error=${
        error instanceof Error ? error.name : "unknown"
      }`,
    );
    return false;
  }
}

/** RFC 9562 version-8 UUID derived from an upstream event's stable identity. */
export async function deterministicEventUuid(value: string): Promise<string> {
  const digest = new Uint8Array(
    await crypto.subtle.digest("SHA-256", new TextEncoder().encode(value)),
  ).slice(0, 16);
  digest[6] = (digest[6]! & 0x0f) | 0x80;
  digest[8] = (digest[8]! & 0x3f) | 0x80;
  const hex = [...digest].map((byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
